import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, FormField } from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ApiError } from '../../api/client';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.email}`);
      const next = params.get('next') || (user.role === 'ADMIN' ? '/admin' : '/dashboard');
      nav(next);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md relative !p-8">
      <div className="text-center mb-8">
        <Link to="/" className="font-display font-bold text-2xl text-gradient">
          AltCoinsMoney
        </Link>
        <h1 className="font-display text-3xl font-bold mt-6">Welcome back</h1>
        <p className="text-ink-600 text-sm mt-2">Sign in to your account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Email" htmlFor="email">
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </FormField>
        <FormField label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </FormField>

        {error && (
          <div className="p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
            {error}
          </div>
        )}

        <Button type="submit" isLoading={loading} className="w-full">
          Sign in
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-ink-600">
        <Link to="/auth/forgot-password" className="text-violet-600 hover:underline">
          Forgot password?
        </Link>
      </div>
      <div className="mt-3 text-center text-sm text-ink-600">
        No account?{' '}
        <Link to="/auth/signup" className="text-violet-600 hover:underline font-semibold">
          Create one
        </Link>
      </div>
    </Card>
  );
}
